import React, { useState, useMemo, useRef, useEffect } from 'react';
import { FaSort, FaSortUp, FaSortDown, FaEllipsisV } from 'react-icons/fa';
import ReactPaginate from 'react-paginate';

const ActionMenu = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="action-menu-container" ref={menuRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button
                onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
                style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#e0e0e0',
                    cursor: 'pointer',
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
                <FaEllipsisV />
            </button>
            {isOpen && (
                <div style={{
                    position: 'absolute',
                    right: 0,
                    top: '100%',
                    marginTop: '4px',
                    background: '#2a2a2a',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                    zIndex: 100,
                    minWidth: '160px',
                    overflow: 'hidden'
                }}>
                    {children}
                </div>
            )}
        </div>
    );
};

const DataGrid = ({
    data,
    columns,
    loading,
    pagination,
    onPageChange,
}) => {
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const sortedData = useMemo(() => {
        if (!data) return [];
        let sortableItems = [...data];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                // Access nested properties if key contains dots (e.g. 'user.firstName')
                const getValue = (obj, path) => path.split('.').reduce((o, i) => (o ? o[i] : null), obj);

                let aValue = getValue(a, sortConfig.key);
                let bValue = getValue(b, sortConfig.key);

                // Handle null/undefined
                if (aValue === null || aValue === undefined) aValue = '';
                if (bValue === null || bValue === undefined) bValue = '';

                // Handle strings (case-insensitive)
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    if (aValue.toLowerCase() < bValue.toLowerCase()) {
                        return sortConfig.direction === 'ascending' ? -1 : 1;
                    }
                    if (aValue.toLowerCase() > bValue.toLowerCase()) {
                        return sortConfig.direction === 'ascending' ? 1 : -1;
                    }
                    return 0;
                }

                // Handle numbers/dates
                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [data, sortConfig]);

    if (loading) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', color: '#fff' }}>
                Loading data...
            </div>
        );
    }

    return (
        <div className="datagrid-container" style={{ position: 'relative', overflowX: 'auto', background: '#1a1a1a', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 10, background: '#2a2a2a' }}>
                    <tr>
                        {columns.map((col, index) => (
                            <th
                                key={index}
                                style={{
                                    padding: '16px',
                                    color: '#3ca151',
                                    fontWeight: '600',
                                    cursor: col.sortable ? 'pointer' : 'default',
                                    whiteSpace: 'nowrap',
                                    borderBottom: '2px solid #333'
                                }}
                                onClick={() => col.sortable && handleSort(col.accessor)}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {col.header}
                                    {col.sortable && (
                                        <span style={{ fontSize: '12px', opacity: 0.7 }}>
                                            {sortConfig.key === col.accessor ? (
                                                sortConfig.direction === 'ascending' ? <FaSortUp /> : <FaSortDown />
                                            ) : (
                                                <FaSort />
                                            )}
                                        </span>
                                    )}
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sortedData.length > 0 ? (
                        sortedData.map((row, rowIndex) => (
                            <tr
                                key={rowIndex}
                                style={{
                                    borderBottom: '1px solid #333',
                                    background: rowIndex % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                                    transition: 'background 0.2s'
                                }}
                            >
                                {columns.map((col, colIndex) => (
                                    <td key={colIndex} style={{ padding: '16px', color: '#e0e0e0', fontSize: '14px', whiteSpace: col.noWrap ? 'nowrap' : 'normal' }}>
                                        {col.render ? col.render(row, ActionMenu) : (
                                            // Basic accessor support for simple usage
                                            col.accessor.split('.').reduce((o, i) => (o ? o[i] : null), row) || 'N/A'
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={columns.length} style={{ padding: '24px', textAlign: 'center', color: '#888' }}>
                                No data found
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {pagination && (
                <div style={{ padding: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                    <ReactPaginate
                        previousLabel={"Previous"}
                        nextLabel={"Next"}
                        breakLabel={"..."}
                        pageCount={pagination.pageCount}
                        marginPagesDisplayed={2}
                        pageRangeDisplayed={5}
                        onPageChange={onPageChange}
                        forcePage={pagination.currentPage}
                        containerClassName={"pagination"}
                        activeClassName={"active"}
                        pageClassName="page-item"
                        pageLinkClassName="page-link"
                        previousClassName="page-item"
                        previousLinkClassName="page-link"
                        nextClassName="page-item"
                        nextLinkClassName="page-link"
                        breakClassName="page-item"
                        breakLinkClassName="page-link"
                    />
                </div>
            )}
        </div>
    );
};

export default DataGrid;
